import { useMemo, useState } from "react";
import {
  Outlet,
  useLocation,
  useOutletContext,
  useParams,
} from "react-router-dom";

import { ArticlePeriodControl } from "./ArticlePeriodControl";
import { DetailBackNav, DetailQueryStatus } from "../../components/DetailPageNav";
import { useSession } from "../../auth/useSession";
import { cleanDescription } from "../../lib/description";
import { useRecordRecentlyViewed } from "../../lib/recentlyViewed";
import type { PriceHistoryRow } from "../../api/articles";
import {
  backTargetFromLocationState,
  type DetailLocationState,
} from "../../lib/backNavigation";

import { ArticleHero } from "./ArticleHero";
import { useArticlePeriodPreference } from "./useArticlePeriodPreference";
import {
  filterArticleRowsByRange,
  resolveArticleDetailPeriod,
  type ArticleDetailPeriodSelection,
} from "./period";
import { latestArticlePriceDate } from "./savings";
import { RawRecordModal, type RawRecordModalData } from "./wholesale";
import { useArticleDetailData } from "./useArticleDetailData";

export type ArticleDetailContext = ReturnType<typeof useArticleDetailData> & {
  rawRecord: RawRecordModalData | null;
  setRawRecord: (record: RawRecordModalData | null) => void;
  articlePeriod: ReturnType<typeof resolveArticleDetailPeriod>;
  periodSelection: ArticleDetailPeriodSelection;
  setPeriodSelection: (selection: ArticleDetailPeriodSelection) => void;
  anchorDate: string | null;
  periodHistoryRows: PriceHistoryRow[];
  periodEvidenceRows: PriceHistoryRow[];
};

const EMPTY_HISTORY_ROWS: readonly PriceHistoryRow[] = [];

export default function ArticleDetailLayout() {
  const { articleKey } = useParams<{ articleKey: string }>();
  const key = articleKey ?? "";
  const data = useArticleDetailData(key);
  const { user } = useSession();
  const location = useLocation();
  const detailState = location.state as DetailLocationState;
  const fromState = detailState?.from;
  const back = backTargetFromLocationState({
    from: fromState,
    fallback: { to: "/articles", label: "← zurück zur Artikel-Suche" },
  });
  const [rawRecord, setRawRecord] = useState<RawRecordModalData | null>(null);
  const periodPreference = useArticlePeriodPreference();
  const { selection: periodSelection, view: periodView } = periodPreference;
  const article = data.articleQuery.data;
  useRecordRecentlyViewed(
    article
      ? {
          kind: "article",
          key,
          label: article.primary_article_code ?? article.article_group_key,
          detail: article.description_example ? cleanDescription(article.description_example) : null,
        }
      : null,
  );
  const historyRows = data.historyQuery.data?.items ?? EMPTY_HISTORY_ROWS;
  const anchorDate = useMemo(() => {
    if (!article) return null;
    return latestArticlePriceDate(article, historyRows);
  }, [article, historyRows]);
  const articlePeriod = useMemo(
    () =>
      resolveArticleDetailPeriod({
        selection: periodSelection,
        anchorDate,
        fiscalYearStartMonth:
          user?.active_membership.fiscal_year_start_month ?? 1,
      }),
    [anchorDate, periodSelection, user?.active_membership.fiscal_year_start_month],
  );
  const setPeriodSelection = (selection: ArticleDetailPeriodSelection) => {
    const resolved = resolveArticleDetailPeriod({ selection, anchorDate, fiscalYearStartMonth: user?.active_membership.fiscal_year_start_month ?? 1 });
    periodPreference.change(selection, resolved.isValid);
  };
  const periodHistoryRows = useMemo(
    () => filterArticleRowsByRange(historyRows, articlePeriod),
    [articlePeriod, historyRows],
  );
  const periodEvidenceRows = useMemo(
    () => filterArticleRowsByRange(data.historyWithWholesale, articlePeriod),
    [articlePeriod, data.historyWithWholesale],
  );

  return (
    <main className="mx-auto max-w-7xl px-6 py-8">
      <DetailBackNav
        back={back}
        home={{ to: "/articles", label: "Artikel-Suche" }}
      />

      <DetailQueryStatus
        isLoading={data.articleQuery.isLoading}
        error={data.articleQuery.isError ? data.articleQuery.error : null}
        loadingLabel="Lade Artikel …"
        notFoundLabel="Artikel nicht gefunden"
        recovery={{ to: "/articles", label: "Artikel suchen" }}
        onRetry={() => void data.articleQuery.refetch()}
      />

      {article ? (
        <>
          <div className="mt-4">
            <ArticleHero article={article} />
          </div>

          <div className="mt-6">
            {periodView.ready && <div className="mb-5"><ArticlePeriodControl selection={periodSelection} period={articlePeriod} anchorDate={anchorDate} onChange={setPeriodSelection} personalView={periodView} /></div>}
            {!periodView.ready ? <p role="status" className="py-8 text-sm text-slate-600">Dein Artikelzeitraum wird geladen …</p> : <Outlet
              context={
                {
                  ...data,
                  rawRecord,
                  setRawRecord,
                  articlePeriod,
                  periodSelection,
                  setPeriodSelection,
                  anchorDate,
                  periodHistoryRows,
                  periodEvidenceRows,
                } satisfies ArticleDetailContext
              }
            />}
          </div>

          <RawRecordModal record={rawRecord} onClose={() => setRawRecord(null)} />
        </>
      ) : null}
    </main>
  );
}

export function useArticleDetailContext(): ArticleDetailContext {
  return useOutletContext<ArticleDetailContext>();
}
